/**
 * Funnel Analytics Page (TASK-238)
 *
 * Visualizes the client funnel: stage distribution, conversion rates,
 * channel/outcome breakdowns and a touchpoint activity trend.
 *
 * Mirrors the structure of AnalyticsPage.tsx (recharts + period selector).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Filter, RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { funnelApi } from '@/services/api/funnel';
import { FUNNEL_ANALYTICS_QUERY_KEY } from '@/services/api/queryKeys';
import type {
  FunnelPeriod,
  FunnelOutcome,
  FunnelAnalytics,
} from '@/types/funnel';

const PERIODS: { value: FunnelPeriod; label: string }[] = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
];

const OUTCOME_OPTIONS: { value: FunnelOutcome | 'all'; label: string }[] = [
  { value: 'all', label: 'Все исходы' },
  { value: 'open', label: 'Открытые' },
  { value: 'won', label: 'Выиграно' },
  { value: 'lost', label: 'Потеряно' },
];

const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Все каналы' },
  { value: 'web', label: 'Web' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'voice', label: 'Голос' },
];

const PIE_COLORS = ['#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6', '#14b8a6'];

function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU');
}

function LoadingState() {
  return (
    <div
      data-testid="funnel-loading"
      className="flex items-center justify-center h-64"
    >
      <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      data-testid="funnel-error"
      className="flex flex-col items-center justify-center h-64 gap-4"
    >
      <AlertCircle className="h-12 w-12 text-red-500" />
      <p className="text-gray-600">Ошибка загрузки воронки</p>
      <Button data-testid="retry-button" onClick={onRetry} variant="outline">
        <RefreshCw className="h-4 w-4 mr-2" />
        Повторить
      </Button>
    </div>
  );
}

export function FunnelPage() {
  const [period, setPeriod] = useState<FunnelPeriod>('30d');
  const [channel, setChannel] = useState<string>('all');
  const [outcome, setOutcome] = useState<FunnelOutcome | 'all'>('all');

  useEffect(() => {
    document.title = 'Воронка - SMETA AI';
  }, []);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...FUNNEL_ANALYTICS_QUERY_KEY, period, channel, outcome],
    queryFn: () =>
      funnelApi.getAnalytics({
        period,
        channel: channel === 'all' ? '' : channel,
        outcome: outcome === 'all' ? '' : outcome,
      }),
  });

  if (isLoading) {
    return (
      <div data-testid="funnel-page" className="px-4 sm:px-6 py-4 sm:py-6">
        <h1 className="text-xl sm:text-3xl font-bold mb-6">Воронка</h1>
        <LoadingState />
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="funnel-page" className="px-4 sm:px-6 py-4 sm:py-6">
        <h1 className="text-xl sm:text-3xl font-bold mb-6">Воронка</h1>
        <ErrorState onRetry={() => refetch()} />
      </div>
    );
  }

  const stats = data as FunnelAnalytics;

  const outcomeData = [
    { name: 'Открытые', value: stats.by_outcome?.open ?? 0 },
    { name: 'Выиграно', value: stats.by_outcome?.won ?? 0 },
    { name: 'Потеряно', value: stats.by_outcome?.lost ?? 0 },
  ];

  return (
    <div data-testid="funnel-page" className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Воронка</h1>
          <p className="text-gray-500 mt-1 hidden sm:block">
            Аналитика жизненного цикла клиентов и конверсии
          </p>
        </div>
        <Button
          data-testid="refresh-button"
          variant="outline"
          size="sm"
          onClick={() => refetch()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Обновить
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
        <div data-testid="period-selector" className="flex gap-2">
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              data-testid={`period-${p.value}`}
              data-active={period === p.value ? 'true' : 'false'}
              variant={period === p.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-gray-400" />
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger data-testid="channel-filter" className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CHANNEL_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={outcome}
            onValueChange={(v) => setOutcome(v as FunnelOutcome | 'all')}
          >
            <SelectTrigger data-testid="outcome-filter" className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTCOME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stages bar chart */}
      <Card data-testid="stages-chart">
        <CardHeader>
          <CardTitle>Распределение клиентов по стадиям</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.stages.length === 0 ? (
            <div
              data-testid="stages-empty"
              className="flex items-center justify-center h-64 text-gray-500"
            >
              Нет данных по стадиям
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={[...stats.stages].sort((a, b) => a.order - b.order)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {/* Accessible textual mirror of the chart */}
              <ul data-testid="stages-list" className="sr-only">
                {stats.stages.map((s) => (
                  <li key={s.codename} data-testid={`stage-row-${s.codename}`}>
                    {s.label}: {s.count}
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* By channel */}
        <Card data-testid="by-channel-chart">
          <CardHeader>
            <CardTitle>По каналам</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.by_channel.length === 0 ? (
              <div
                data-testid="by-channel-empty"
                className="flex items-center justify-center h-64 text-gray-500"
              >
                Нет данных по каналам
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.by_channel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="channel" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By outcome */}
        <Card data-testid="by-outcome-chart">
          <CardHeader>
            <CardTitle>По исходам</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={outcomeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label
                >
                  {outcomeData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Conversion rates */}
        <Card data-testid="conversion-rates">
          <CardHeader>
            <CardTitle>Конверсия между стадиями</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.conversion_rates.length === 0 ? (
              <div
                data-testid="conversion-empty"
                className="flex items-center justify-center h-64 text-gray-500"
              >
                Нет данных о конверсии
              </div>
            ) : (
              <div className="space-y-4">
                {stats.conversion_rates.map((r, index) => (
                  <div
                    key={`${r.from_codename}-${r.to_codename}`}
                    className="space-y-1"
                    data-testid={`conversion-row-${index}`}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {r.from_codename} → {r.to_codename}
                      </span>
                      <span className="text-gray-600">
                        {Math.round(r.rate * 100)}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{
                          width: `${Math.min(100, Math.max(0, r.rate * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trend */}
        <Card data-testid="trend-chart">
          <CardHeader>
            <CardTitle>Динамика обращений</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.trend.length === 0 ? (
              <div
                data-testid="trend-empty"
                className="flex items-center justify-center h-64 text-gray-500"
              >
                Нет данных о динамике
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={stats.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer summary */}
      <p className="text-sm text-gray-400">
        Всего обращений за период:{' '}
        {formatNumber(stats.trend.reduce((acc, t) => acc + t.count, 0))}
      </p>
    </div>
  );
}

export default FunnelPage;
